import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { collectFormErrors } from '@shared/formHelpers';
import { BusinessActivitySurvey, CountryMetadata } from '@shared/models/data-model';
import { AmplitudeService } from '@shared/services/amplitude.service';
import { CountryService } from '@shared/services/country.service';
import { DTRUM_ACTION_NAME, DynatraceHelperService } from '@shared/services/dynatraceHelper.service';
import { GoogleTagManagerService } from '@shared/services/google-tag-manager.service';
import { LoadingScreenService } from '@shared/services/loading-screen.service';
import { RegistrationService } from '@shared/services/registration.service';
import { REGISTRATION_STEP_ID } from '@shared/services/steps.service';
import { NGXLogger } from 'ngx-logger';

@Component({
  selector: 'international-activity',
  templateUrl: './international-activity.component.html',
  styleUrls: []
})
export class InternationalActivityComponent implements OnInit {
    private readonly subStepName = "Company International Activity";
    private businessActivitySurveyToSave: BusinessActivitySurvey;
    loading = false;
    form: UntypedFormGroup;
    internationalCountriesList: CountryMetadata[];

    constructor(
        private readonly fb: UntypedFormBuilder,
        private readonly registrationService: RegistrationService,
        private readonly countryService: CountryService,
        private readonly amplitudeService: AmplitudeService,
        private readonly gtmService: GoogleTagManagerService,
        private readonly logger: NGXLogger,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly dynatrace: DynatraceHelperService,
        private readonly destroyRef: DestroyRef,
        private readonly translate: TranslateService,
        private readonly loadingScreenService: LoadingScreenService
    ) { }

    ngOnInit(): void {
        this.trackSubStepViewed();
        const businessActivitySurvey: BusinessActivitySurvey = this.route.snapshot.data.businessActivitySurvey;
        this.businessActivitySurveyToSave = {
            ...businessActivitySurvey
        };
        const countryOfIncorporation: string = this.route.snapshot.data.company?.registeredAddress?.countryISO;
        this.setCountries(this.route.snapshot.data.countryList, countryOfIncorporation);

        const allCountries = this.route.snapshot.data.countryList;
        this.setCountries(allCountries, countryOfIncorporation);
        this.translate.onLangChange.pipe(
            takeUntilDestroyed(this.destroyRef)
        ).subscribe(async () => {
            const showLoadingScreen = Boolean(this.form?.get("primaryInternationalMarket"));
            if (showLoadingScreen) {
                this.loadingScreenService.show('LOADING_SCREEN.COUNTRY_TRANSLATION');
            }

            const allCountries = await this.countryService.getCurrentCountries();
            this.setCountries(allCountries, countryOfIncorporation);

            if (showLoadingScreen) {
                this.loadingScreenService.hide();
            }
        });

        const isInternational = Boolean(businessActivitySurvey?.internationalActivity);

        this.form = this.fb.group({
            "isInternational": [isInternational, [Validators.required]]
        });

        if (isInternational) {
            this.showInternationalMarketsList(businessActivitySurvey?.internationalActivityPrimaryCountry);
        }

        this.form.get("isInternational").valueChanges.subscribe((value) => {
            if (value) {
                this.showInternationalMarketsList();
            } else if (this.form.get("primaryInternationalMarket")) {
                this.hideInternationalMarketsList();
            }
        });
    }

    private setCountries(allCountries: CountryMetadata[], countryOfIncorporation: string) {
        allCountries.sort(this.countryService.sortFunction);
        this.internationalCountriesList = allCountries.filter(c => c.icc)
            .map(c => (c.iso3 === countryOfIncorporation) ? { ...c, disabled: true } : c);
    }

    private showInternationalMarketsList(value: string = null) {
        if (!this.form.get("primaryInternationalMarket")) {
            this.form.addControl("primaryInternationalMarket", new UntypedFormControl(value, [
                Validators.required
            ]));
        }
    }

    private hideInternationalMarketsList() {
        this.form.removeControl("primaryInternationalMarket");
    }

    private isValid(): boolean {
        this.form.markAllAsTouched();
        return this.form.valid;
    }

    previousStep(): void {
        const backActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.BACK_TO_PREVIOUS);
        this.router.navigate([this.registrationService.getPreviousStep()]).then(() => {
            this.dynatrace.leaveAction(backActionId);
        });
    }

    async nextStep() {
        if (this.isValid()) {
            this.loading = true;
            const formValues = this.form.value;
            this.businessActivitySurveyToSave.internationalActivity = formValues.isInternational;
            this.businessActivitySurveyToSave.internationalActivityPrimaryCountry = formValues.primaryInternationalMarket;

            const submitActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.SUBMIT_FORM);

            try {
                await this.registrationService.saveBusinessActivitySurvey(this.businessActivitySurveyToSave);
                this.trackSubStepCompleted();
                this.registrationService.completeStep(REGISTRATION_STEP_ID.INTERNATIONAL_ACTIVITY);
                this.router.navigate([this.registrationService.getNextStep()]).then(() => {
                    this.dynatrace.leaveAction(submitActionId);
                });
            } catch (e) {
                this.logger.error('Save business activity survey failed', e, 'with business activity survey', this.businessActivitySurveyToSave);
                this.loading = false;
                this.dynatrace.leaveAction(submitActionId);
            }
        } else {
            this.amplitudeService.trackFormSubmissionRejected(this.route.snapshot.data['pageName'], collectFormErrors(this.form));
        }
    }

    private trackSubStepViewed(): void {
        this.amplitudeService.trackSubStepViewed(this.subStepName);
        this.gtmService.trackSubStepViewed(this.subStepName);
    }

    private trackSubStepCompleted(): void {
        this.amplitudeService.trackSubStepCompleted(this.subStepName);
        this.gtmService.trackSubStepCompleted(this.subStepName);
    }

}
